import { create } from 'zustand';
import { importApi } from '../services/api';
import type { ImportTask } from '../types';
import type { PagedData, ListQueryRequest } from '../types';

interface ImportState {
  // 状态
  tasks: ImportTask[];
  currentTask: ImportTask | null;
  total: number;
  isLoading: boolean;
  uploadProgress: number;
  error: string | null;
  
  // Actions
  fetchTasks: (params: ListQueryRequest) => Promise<void>;
  fetchTaskById: (id: string) => Promise<void>;
  uploadFile: (dimensionCode: string, file: File) => Promise<void>;
  downloadTemplate: (dimensionCode: string) => Promise<void>;
  downloadErrorReport: (taskId: string) => Promise<void>;
  setUploadProgress: (progress: number) => void;
  setCurrentTask: (task: ImportTask | null) => void;
}

export const useImportStore = create<ImportState>((set, get) => ({
  tasks: [],
  currentTask: null,
  total: 0,
  isLoading: false,
  uploadProgress: 0,
  error: null,

  fetchTasks: async (params: ListQueryRequest) => {
    set({ isLoading: true, error: null });
    try {
      const data = await importApi.getImportTasks(params);
      set({ 
        tasks: data.items || [], 
        total: data.total || 0,
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  fetchTaskById: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      const task = await importApi.getImportTaskById(id);
      set({ 
        currentTask: task, 
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  uploadFile: async (dimensionCode: string, file: File) => {
    set({ isLoading: true, uploadProgress: 0, error: null });
    try {
      await importApi.uploadFile(dimensionCode, file, (progress) => {
        set({ uploadProgress: progress });
      });
      set({ isLoading: false, uploadProgress: 100 });
    } catch (error: any) {
      set({ error: error.message, isLoading: false, uploadProgress: 0 });
      throw error;
    }
  },

  downloadTemplate: async (dimensionCode: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await importApi.downloadTemplate(dimensionCode);
      const blob = response as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dimensionCode}_template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  downloadErrorReport: async (taskId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await importApi.downloadErrorReport(taskId);
      const blob = response as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `error_report_${taskId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  setUploadProgress: (progress: number) => {
    set({ uploadProgress: progress });
  },

  setCurrentTask: (task: ImportTask | null) => {
    set({ currentTask: task });
  },
}));
